/**
 * RxDB Bootstrap Migration
 *
 * On first post-login boot, migrates legacy Dexie data into RxDB collections so
 * the user's local data is available before the first replication pull completes.
 *
 * Flow:
 *  1. Check localStorage flag — if set, skip (already done).
 *  2. Detect legacy Dexie DB `macrotracker:user:{userId}`. If absent, mark done.
 *  3. Drain pending legacy mutations via syncService.sync() (retries once).
 *     If drain fails and there are pending items → abort and surface a banner.
 *  4. Read all Dexie records and bulk-insert into the matching RxDB collections.
 *  5. Set localStorage flag.
 *  6. Delete the legacy Dexie DB.
 */

import Dexie from 'dexie';
import { getDb } from './database';
import {
  DIARY_DAYS_COLLECTION,
  RECIPES_COLLECTION,
  USER_FOODS_COLLECTION,
  GOALS_HISTORY_COLLECTION,
  WEIGHT_HISTORY_COLLECTION,
  SETTINGS_COLLECTION,
  SERVER_MODIFIER_ID,
  type DiaryDayDocType,
  type RecipeDocType,
  type UserFoodDocType,
  type GoalHistoryDocType,
  type WeightHistoryDocType,
  type SettingDocType,
  type SettingTypeEnum,
  type SettingValueMap,
} from '@macro-tracker/models';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIGRATION_FLAG_PREFIX = 'rxdb:migrated:';
const LEGACY_DB_PREFIX = 'macrotracker:user:';

// Legacy Dexie namespace → RxDB collection mapping
const NAMESPACE_MAP = {
  'macrotracker:diary-days': DIARY_DAYS_COLLECTION,
  'macrotracker:recipes': RECIPES_COLLECTION,
  'macrotracker:food-items': USER_FOODS_COLLECTION,
  'macrotracker:goals-history': GOALS_HISTORY_COLLECTION,
  'macrotracker:weight-history': WEIGHT_HISTORY_COLLECTION,
  'macrotracker:settings': SETTINGS_COLLECTION,
} as const;

// ---------------------------------------------------------------------------
// Legacy Dexie types
// ---------------------------------------------------------------------------

interface LegacyRecord {
  namespace: string;
  id: string;
  value: unknown;
}

// ---------------------------------------------------------------------------
// Shape converters: legacy entity → RxDB doc
// Each converter is intentionally lenient about missing fields.
// ---------------------------------------------------------------------------

const todiaryDayDoc = (entity: any): DiaryDayDocType | null => {
  if (!entity?.id) return null;
  return {
    id: entity.id,
    userId: entity.userId ?? '',
    date: entity.date ?? '',
    body: entity.body ?? { goalSnapshot: null, meals: [], importedTotals: null, planGeneration: { generatedForDay: false, generatedAt: null, lastInputHash: null, status: 'idle', errorMessage: null, plannerDiagnostics: null } },
    createdAt: entity.createdAt ?? new Date().toISOString(),
    updatedAt: entity.updatedAt ?? new Date().toISOString(),
    deletedAt: entity.deletedAt ?? null,
    syncRevision:
      typeof entity.syncRevision === 'number'
        ? entity.syncRevision
        : typeof entity.revision === 'number'
          ? entity.revision
          : 0,
    modifiedBy: typeof entity.modifiedBy === 'string' ? entity.modifiedBy : SERVER_MODIFIER_ID,
  };
};

const toRecipeDoc = (entity: any): RecipeDocType | null => {
  if (!entity?.id) return null;
  // Legacy recipe may already be in { body: RecipeBody } shape or the flat Recipe shape.
  const hasBody = entity.body && typeof entity.body === 'object' && !Array.isArray(entity.body);
  const body = hasBody
    ? entity.body
    : {
        name: entity.name ?? '',
        yieldMode: entity.yield?.mode ?? (entity.servings != null ? 'count' : 'weight'),
        servings: entity.yield?.mode === 'count' ? entity.yield.servings : entity.servings,
        servingLabel:
          entity.yield?.mode === 'count' ? entity.yield.servingLabel : entity.servingDescription,
        servingSizes: Array.isArray(entity.servingSizes) ? entity.servingSizes : [],
        preparedWeightGrams:
          entity.yield?.mode === 'weight'
            ? entity.yield.servingWeightGrams
            : (entity.preparedWeightGramsManual ?? entity.preparedWeightGramsAuto ?? null),
        preparedWeightGramsAuto: entity.preparedWeightGramsAuto ?? null,
        preparedWeightGramsManual: entity.preparedWeightGramsManual ?? null,
        displayWeightUnit: entity.displayWeightUnit ?? null,
        ingredients: Array.isArray(entity.ingredients) ? entity.ingredients : [],
        isArchived: entity.isArchived === true,
      };
  return {
    id: entity.id,
    userId: entity.userId ?? '',
    body,
    createdAt: entity.createdAt ?? new Date().toISOString(),
    updatedAt: entity.updatedAt ?? new Date().toISOString(),
    deletedAt: entity.deletedAt ?? (entity.isArchived === true ? new Date().toISOString() : null),
  };
};

const toUserFoodDoc = (entity: any, userId: string): UserFoodDocType | null => {
  if (!entity?.id) return null;
  // Legacy FoodItem has servingSizes[0] or flat nutrition fields
  const serving = entity.servingSizes?.[0];
  return {
    id: entity.id,
    userId: entity.userId ?? userId,
    name: entity.name ?? '',
    brand: entity.brand ?? null,
    upc: entity.upc ?? null,
    calories: serving?.nutrition?.calories ?? entity.calories ?? 0,
    protein: serving?.nutrition?.protein ?? entity.protein ?? 0,
    carbs: serving?.nutrition?.carbs ?? entity.carbs ?? 0,
    fat: serving?.nutrition?.fat ?? entity.fat ?? 0,
    servingSize: serving?.grams ?? entity.servingSize ?? null,
    servingUnit: serving?.label ?? entity.servingUnit ?? 'serving',
    isFavorite: entity.isFavorite ?? false,
    isArchived: entity.isArchived ?? false,
    isProvisional: entity.isProvisional ?? false,
    isSharedByOwner: entity.isSharedByOwner ?? false,
    createdAt: entity.createdAt ?? new Date().toISOString(),
    updatedAt: entity.updatedAt ?? new Date().toISOString(),
  };
};

const toGoalDoc = (entity: any, userId: string): GoalHistoryDocType | null => {
  if (!entity?.id) return null;
  return {
    id: entity.id,
    userId: entity.userId ?? userId,
    effectiveFromDate: entity.effectiveFromDate ?? '',
    goalType: entity.goalType ?? 'fixed',
    caloriesTarget: entity.caloriesTarget ?? 0,
    strategyType: entity.strategyType ?? 'percent',
    proteinPct: entity.proteinPct ?? null,
    carbsPct: entity.carbsPct ?? null,
    fatPct: entity.fatPct ?? null,
    proteinGPerKg: entity.proteinGPerKg ?? null,
    carbsGPerKg: entity.carbsGPerKg ?? null,
    fatGPerKg: entity.fatGPerKg ?? null,
    flexMacro: entity.flexMacro ?? null,
    fixedProteinG: entity.fixedProteinG ?? null,
    fixedCarbsG: entity.fixedCarbsG ?? null,
    fixedFatG: entity.fixedFatG ?? null,
    bmrSettings: entity.bmrSettings ?? null,
    createdAtUtc: entity.createdAtUtc ?? new Date().toISOString(),
    updatedAtUtc: entity.updatedAtUtc ?? new Date().toISOString(),
    deletedAt: entity.deletedAt ?? null,
  };
};

const toWeightDoc = (entity: any, userId: string): WeightHistoryDocType | null => {
  if (!entity?.id) return null;
  return {
    id: entity.id,
    userId: entity.userId ?? userId,
    weightKg: entity.weightKg ?? 0,
    measuredAtUtc: entity.measuredAtUtc ?? new Date().toISOString(),
    localDate: entity.localDate ?? entity.measuredAtUtc?.slice(0, 10) ?? '',
    source: entity.source ?? null,
    notes: entity.notes ?? null,
    bodyFatPct: entity.bodyFatPct ?? null,
    measurements: entity.measurements ?? null,
    createdAtUtc: entity.createdAtUtc ?? new Date().toISOString(),
    updatedAtUtc: entity.updatedAtUtc ?? new Date().toISOString(),
    deletedAt: entity.deletedAt ?? null,
  };
};

/** Legacy per-key setting entity shape from old Dexie storage. */
interface LegacySettingEntity {
  type?: string;
  key?: string;
  value?: string | number | boolean | null | object;
  updatedAt?: string;
}

/**
 * Convert per-key UserSetting records into per-type SettingDocType docs.
 * Legacy: [{ id: "featureFlag:enableAiPhotos", type, key, value, updatedAt }, ...]
 * RxDB:   [{ id: "${userId}:feature-flags", userId, type: "feature-flags", value: { enableAiPhotos: true }, updatedAt }, ...]
 */
const toSettingDocs = (entities: LegacySettingEntity[], userId: string): SettingDocType[] => {
  const typeMap: Partial<Record<SettingTypeEnum, { value: SettingValueMap; updatedAt: string }>> = {};
  const settingTypeToDocType: Record<string, SettingTypeEnum> = {
    featureFlag: 'feature-flags',
    onboarding: 'onboarding',
    userPreference: 'preferences',
  };
  const isSettingTypeEnum = (value: string): value is SettingTypeEnum =>
    value === 'feature-flags' || value === 'onboarding' || value === 'preferences';

  for (const entity of entities) {
    if (!entity?.type || !entity?.key) continue;
    const docType = settingTypeToDocType[entity.type] ?? entity.type;
    if (!isSettingTypeEnum(docType)) continue;
    if (!typeMap[docType]) {
      typeMap[docType] = { value: {}, updatedAt: entity.updatedAt ?? new Date().toISOString() };
    }
    if (entity.value !== undefined) {
      typeMap[docType].value[entity.key] = entity.value;
    }
    // Keep the newest updatedAt for the doc
    if (entity.updatedAt && entity.updatedAt > typeMap[docType].updatedAt) {
      typeMap[docType].updatedAt = entity.updatedAt;
    }
  }

  return (Object.entries(typeMap) as Array<[SettingTypeEnum, { value: SettingValueMap; updatedAt: string }]>).map(([docType, { value, updatedAt }]) => ({
    id: `${userId}:${docType}`,
    userId,
    type: docType,
    value,
    updatedAt,
  }));
};

// ---------------------------------------------------------------------------
// Main bootstrap entry point
// ---------------------------------------------------------------------------

export type BootstrapResult =
  | { status: 'skipped' }
  | { status: 'completed' }
  | { status: 'failed'; reason: string };

/**
 * Run the one-time Dexie → RxDB bootstrap migration for the given user.
 *
 * @param userId   Authenticated user ID
 * @param onProgress  Optional progress callback (0–1)
 */
export async function bootstrapRxDb(
  userId: string,
  onProgress?: (fraction: number, label: string) => void,
): Promise<BootstrapResult> {
  const migrationFlag = `${MIGRATION_FLAG_PREFIX}${userId}`;

  // 1. Already migrated?
  if (localStorage.getItem(migrationFlag)) {
    return { status: 'skipped' };
  }

  const legacyDbName = `${LEGACY_DB_PREFIX}${userId}`;

  // 2. Check if legacy DB exists
  const existingDbs = await Dexie.getDatabaseNames();
  if (!existingDbs.includes(legacyDbName)) {
    // No legacy data — mark done and proceed straight to RxDB
    localStorage.setItem(migrationFlag, '1');
    return { status: 'completed' };
  }

  onProgress?.(0.05, 'Opening legacy database…');

  // Open legacy DB (read-only — we only remove it at the end)
  const legacyDb = new Dexie(legacyDbName);
  legacyDb.version(1).stores({ records: '&[namespace+id], namespace, id' });
  legacyDb.version(2).stores({ 'sync-records': '&id, namespace, entityId, operation' });
  legacyDb.version(3).stores({
    records: '&[namespace+id], namespace, id',
    'sync-records': '&id, namespace, entityId, operation',
  });
  legacyDb.version(4).stores({
    records: '&[namespace+id], namespace, id',
    'sync-records': '&id, namespace, entityId, operation',
    'orphaned-legacy-records': '++autoId, namespace, originalId, recordedAt',
  });

  try {
    // 3. Drain pending legacy mutations (best-effort)
    onProgress?.(0.1, 'Draining pending sync queue…');
    await drainLegacySyncQueue();

    // 4. Read all legacy records
    onProgress?.(0.2, 'Reading legacy data…');
    const allRecords = await (legacyDb.table('records') as Dexie.Table<LegacyRecord>).toArray();

    // Group records by namespace
    const byNamespace = new Map<string, any[]>();
    for (const record of allRecords) {
      if (!byNamespace.has(record.namespace)) byNamespace.set(record.namespace, []);
      byNamespace.get(record.namespace)!.push(record.value);
    }

    // 5. Bulk-insert into RxDB
    const rxdb = await getDb(userId);
    const namespaces = Object.keys(NAMESPACE_MAP) as (keyof typeof NAMESPACE_MAP)[];
    let step = 0;

    for (const namespace of namespaces) {
      step++;
      const fraction = 0.2 + (step / namespaces.length) * 0.65;
      const collectionName = NAMESPACE_MAP[namespace];
      onProgress?.(fraction, `Migrating ${collectionName}…`);

      const entities = byNamespace.get(namespace) ?? [];
      if (entities.length === 0) continue;

      try {
        if (namespace === 'macrotracker:diary-days') {
          const docs = entities.map(todiaryDayDoc).filter(Boolean) as DiaryDayDocType[];
          if (docs.length) await rxdb[DIARY_DAYS_COLLECTION].bulkInsert(docs);
        } else if (namespace === 'macrotracker:recipes') {
          const docs = entities.map(toRecipeDoc).filter(Boolean) as RecipeDocType[];
          if (docs.length) await rxdb[RECIPES_COLLECTION].bulkInsert(docs);
        } else if (namespace === 'macrotracker:food-items') {
          const docs = entities
            .map((e) => toUserFoodDoc(e, userId))
            .filter(Boolean) as UserFoodDocType[];
          if (docs.length) await rxdb[USER_FOODS_COLLECTION].bulkInsert(docs);
        } else if (namespace === 'macrotracker:goals-history') {
          const docs = entities
            .map((e) => toGoalDoc(e, userId))
            .filter(Boolean) as GoalHistoryDocType[];
          if (docs.length) await rxdb[GOALS_HISTORY_COLLECTION].bulkInsert(docs);
        } else if (namespace === 'macrotracker:weight-history') {
          const docs = entities
            .map((e) => toWeightDoc(e, userId))
            .filter(Boolean) as WeightHistoryDocType[];
          if (docs.length) await rxdb[WEIGHT_HISTORY_COLLECTION].bulkInsert(docs);
        } else if (namespace === 'macrotracker:settings') {
          const docs = toSettingDocs(entities, userId);
          if (docs.length) await rxdb[SETTINGS_COLLECTION].bulkInsert(docs);
        }
      } catch (err) {
        // bulkInsert errors (e.g. duplicate IDs) are non-fatal — RxDB deduplicates.
        console.warn(`[bootstrap] Non-fatal error migrating ${collectionName}:`, err);
      }
    }

    onProgress?.(0.9, 'Finalizing migration…');

    // 6. Mark migration complete
    localStorage.setItem(migrationFlag, '1');

    // 7. Delete legacy Dexie DB
    try {
      legacyDb.close();
      await Dexie.delete(legacyDbName);
    } catch (err) {
      // Non-fatal — will be retried next time (flag prevents re-running the migration)
      console.warn('[bootstrap] Could not delete legacy Dexie DB:', err);
    }

    onProgress?.(1, 'Migration complete');
    return { status: 'completed' };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    console.error('[bootstrap] Migration failed:', err);
    return { status: 'failed', reason };
  } finally {
    try { legacyDb.close(); } catch { /* ignore */ }
  }
}

// ---------------------------------------------------------------------------
// Legacy sync drain helper
// ---------------------------------------------------------------------------

async function drainLegacySyncQueue(): Promise<void> {
  // syncService has been removed — legacy sync infrastructure deleted.
  // RxDB replication handles push/pull from here on; drain is a no-op.
}

// ---------------------------------------------------------------------------
// Migration flag helpers (exposed for testing and forced re-runs)
// ---------------------------------------------------------------------------

export const isMigrated = (userId: string): boolean =>
  !!localStorage.getItem(`${MIGRATION_FLAG_PREFIX}${userId}`);

export const clearMigrationFlag = (userId: string): void =>
  localStorage.removeItem(`${MIGRATION_FLAG_PREFIX}${userId}`);
